/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { PUBLIC_ROUTES } from '../context/PermissionContext';

/**
 * A wrapper for routes that requires authentication and optionally specific permissions.
 * Uses global permission context for consistent permission checking.
 */
const ProtectedRoute = ({ children, permissions = [], requireAll = false }) => {
  const { user, loading: authLoading } = useAuth();
  const { canAccessRoute, loading: permissionLoading } = usePermissions();
  const location = useLocation();

  // Show loading state while checking authentication and permissions
  if (authLoading || permissionLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4 bg-slate-950/5">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Loading...</p>
      </div>
    );
  }

  // Check if user is authenticated
  if (!user) {
    // Redirect to login but save the current location to return after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check permissions if any are required
  if (permissions.length > 0) {
    const permitted = canAccessRoute(location.pathname, permissions);
    if (!permitted) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

export default ProtectedRoute;

ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  permissions: PropTypes.arrayOf(PropTypes.string),
  requireAll: PropTypes.bool,
};
